import type { HTMLAttributes, ReactNode } from 'react';

import { cn } from '@shared/lib/cn';

import {
  CARD_BASE_CLASS,
  CARD_GAP_CLASS,
  CARD_PADDING_CLASS,
  CARD_SHADOW_CLASS,
  type CardGap,
  type CardPadding,
  type CardShadow,
  type CardStatus,
} from './card.constant';
import { CardSkeleton } from './card.skeleton.ui';

export type CardTag = 'div' | 'section' | 'article' | 'li';

export interface CardProps extends HTMLAttributes<HTMLElement> {
  /** Figma `shadow`. 기본 default */
  shadow?: CardShadow;
  /** 안쪽 여백: none 0 · sm 16 · md 20 · lg 24 · xl 32. 기본 md(Figma) */
  padding?: CardPadding;
  /** 내용 사이 간격: none 0 · sm 10 · md 16 · lg 20. 기본 md(Figma) */
  gap?: CardGap;
  /** Figma `status`. init = 막대 3줄 스켈레톤 */
  status?: CardStatus;
  /** 그릴 태그. 기본 div */
  as?: CardTag;
  /** Figma `Content`(바꿔끼우기) — 카드 안 내용 */
  children?: ReactNode;
}

/**
 * 카드(면). 흰 면 · 모서리 12 · 안쪽 여백 20 · 그림자. 너비는 부모가 정한다(Figma 예시 360).
 * 내용 사이 간격은 16(세로로 쌓인다). 여백 · 간격은 `padding` · `gap` 으로 고르고,
 * 위아래 · 좌우가 다른 여백이나 가로 배치는 `className` 으로 덮는다(`cn` 이 뒤의 클래스를 남긴다).
 */
export function Card({
  shadow = 'default',
  padding = 'md',
  gap = 'md',
  status = 'default',
  as: Tag = 'div',
  className,
  children,
  ...rest
}: CardProps) {
  if (status === 'init') {
    return (
      <CardSkeleton
        shadow={shadow}
        className={cn(CARD_PADDING_CLASS[padding], CARD_GAP_CLASS[gap], className)}
      />
    );
  }
  return (
    <Tag
      data-shadow={shadow}
      data-padding={padding}
      {...rest}
      className={cn(
        CARD_BASE_CLASS,
        CARD_PADDING_CLASS[padding],
        CARD_GAP_CLASS[gap],
        CARD_SHADOW_CLASS[shadow],
        className,
      )}
    >
      {children}
    </Tag>
  );
}
